import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import unionBy from 'lodash/unionBy';

import { IssueTestType } from '@qality/common-model/src/TestType';
import {
  StorageOfUniqueServices,
  TestCasesService,
} from '@qality/common-service';

export type IssueTestTypeDetails = {
  issueTestType: IssueTestType;
  isLoading: boolean;
};

type IssueTestTypeContextValue = {
  getIssueTestTypeDetails: (
    issueId: number
  ) => IssueTestTypeDetails | undefined;
  updateIssueTestTypeLoading: (
    issueTestType: IssueTestType,
    loading: boolean
  ) => void;
  updateIssueTestType: (issueTestType: IssueTestType) => void;
};

export const initialContextValue: IssueTestTypeContextValue = {
  getIssueTestTypeDetails: () => undefined,
  updateIssueTestTypeLoading: () => undefined,
  updateIssueTestType: () => undefined,
};

export const IssueTestTypeContext =
  createContext<IssueTestTypeContextValue>(initialContextValue);

export const useIssueTestTypeContext = (): IssueTestTypeContextValue => {
  return useContext(IssueTestTypeContext);
};

type Props = {
  testCaseIds: number[];
  children: React.ReactNode;
};

export default function IssueTestTypeProvider({
  testCaseIds,
  children,
}: Props): JSX.Element {
  const [issueTestTypes, setIssueTestTypes] = useState<
    Array<IssueTestTypeDetails>
  >([]);
  const IS_ADVANCED = StorageOfUniqueServices.get('LicenseService').isAdvanced;
  const updateQueue = useRef(Promise.resolve());
  const [currentTestCaseIds, setCurrentTestCaseIds] = useState<number[]>([]);

  useEffect(() => {
    if (IS_ADVANCED && !isEqual(testCaseIds, currentTestCaseIds)) {
      handleAddingNewTestCases();
      handleRemovingTestCases();
    }
    setCurrentTestCaseIds(testCaseIds);
  }, [testCaseIds]);

  const getFetchedTestCaseIds = (): number[] => {
    return issueTestTypes.map((item) => item.issueTestType.testCaseId);
  };

  const handleAddingNewTestCases = async (): Promise<void> => {
    const newTestCaseIds = new Set(
      difference(testCaseIds, getFetchedTestCaseIds())
    );
    if (newTestCaseIds.size > 0) {
      queueUpdate(async () => {
        await initializeIssueTestTypesData(Array.from(newTestCaseIds));
      });
    }
  };

  const handleRemovingTestCases = async (): Promise<void> => {
    const deletedTestCaseIds = difference(getFetchedTestCaseIds(), testCaseIds);
    if (deletedTestCaseIds.length > 0) {
      queueUpdate(async () => {
        setIssueTestTypes((prevIssueTestTypes) =>
          prevIssueTestTypes.filter(
            (item) =>
              !deletedTestCaseIds.includes(item.issueTestType.testCaseId)
          )
        );
      });
    }
  };

  const queueUpdate = (updateFn: () => Promise<void>) => {
    updateQueue.current = updateQueue.current.then(() => updateFn());
  };

  const initializeIssueTestTypesData = async (
    issueIds: number[]
  ): Promise<void> => {
    const result = await fetchTestTypesForIssues(issueIds);

    const newIssueTestTypes = result.map((issueTestType) => ({
      issueTestType,
      isLoading: false,
    }));

    setIssueTestTypes((prevIssueTestTypes) =>
      unionBy(
        prevIssueTestTypes,
        newIssueTestTypes,
        (item) => item.issueTestType.testCaseId
      )
    );
  };

  const fetchTestTypesForIssues = async (
    issueIds: number[]
  ): Promise<Array<IssueTestType>> => {
    try {
      return await TestCasesService.getIssueTestTypes(issueIds);
    } catch (error) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        error,
        'We could not retrieve test types.'
      );
      return [];
    }
  };

  const getIssueTestTypeDetails = (
    issueId: number
  ): IssueTestTypeDetails | undefined => {
    return issueTestTypes.find(
      (details) => details.issueTestType.testCaseId === issueId
    );
  };

  const updateIssueTestType = async (
    issueTestType: IssueTestType
  ): Promise<void> => {
    try {
      updateIssueTestTypeLoading(issueTestType, true);
      await TestCasesService.bulkUpdateTestCaseTestTypes([
        { id: issueTestType.testCaseId, testTypeId: issueTestType.testTypeId },
      ]);
      setIssueTestTypes((prevDetails) => {
        const updatedDetails = prevDetails.map((detail) => {
          if (detail.issueTestType.testCaseId === issueTestType.testCaseId) {
            return { ...detail, issueTestType };
          }
          return detail;
        });
        return updatedDetails;
      });
    } catch (error) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        error,
        'Failed to update test case test type'
      );
    } finally {
      updateIssueTestTypeLoading(issueTestType, false);
    }
  };

  const updateIssueTestTypeLoading = (
    issueTestType: IssueTestType,
    loading: boolean
  ): void => {
    setIssueTestTypes((prevDetails) => {
      const updatedDetails = prevDetails.map((detail) => {
        if (detail.issueTestType.testCaseId === issueTestType.testCaseId) {
          return { ...detail, isLoading: loading };
        }
        return detail;
      });
      return updatedDetails;
    });
  };

  return (
    <IssueTestTypeContext.Provider
      value={{
        getIssueTestTypeDetails,
        updateIssueTestType,
        updateIssueTestTypeLoading,
      }}
    >
      {children}
    </IssueTestTypeContext.Provider>
  );
}
